import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GridSortModel } from '@mui/x-data-grid'
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  MutationDefinition,
} from '@reduxjs/toolkit/dist/query'
import { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks'

import { DeleteGantt, DeleteGanttByCode } from '@pages/GanttChart/types'

import { useSorting } from '@hooks'
import { ROUTES } from '@constants'
import {
  ConfigRowsType,
  CopyModelDataType,
  DataOfCopiedModelType,
  ExtraOptions,
  ViewShortDTO,
} from '@types'

import { GanttCopyDataPayload } from '@gantt/components/GanttCreateOrEdit'

type GantCopyType = MutationTrigger<
  MutationDefinition<
    GanttCopyDataPayload,
    BaseQueryFn<FetchArgs, unknown, FetchBaseQueryError, ExtraOptions>,
    string,
    number,
    'commonApi'
  >
>

type UseHandlersParams = {
  deleteGantt: DeleteGantt
  deleteGanttById: DeleteGanttByCode
  copyGantt: GantCopyType
}

export const useHandlers = ({ deleteGantt, deleteGanttById, copyGantt }: UseHandlersParams) => {
  const navigate = useNavigate()
  const { sort, setSort } = useSorting()
  const [objectCode, setObjectCode] = useState<string>()
  const [searchPattern, setSearchPattern] = useState<string>()
  const [currentSort, setCurrentSort] = useState<GridSortModel>(sort || [])
  const [allGantt, setAllGantt] = useState<ViewShortDTO[]>([])
  const [copyGanttData, setCopyGanttData] = useState<DataOfCopiedModelType | null>(null)
  const [showCopyDialog, setShowCopyDialog] = useState(false)

  const handleDeleteGantt = (id: string | number) => deleteGantt(id)

  const handleDeleteGanttByCode = (code: string | number) => deleteGanttById(code)

  const handleEditGantt = (id: string) => {
    navigate(`${ROUTES.CONFIG_GANTT_EDIT}/${id}`)
  }

  const handleSetSorting = (value: GridSortModel | null) => {
    setSort(value ? `${value[0].field},${value[0].sort}` : undefined)
    setCurrentSort(value ? value : [])
  }

  const handleSetObjectCode = (code: string) => setObjectCode(code)

  const handleSetSearchPattern = (pattern: string) => {
    setSearchPattern(pattern)
  }

  const handleSetCurrentSort = (value: GridSortModel) => setCurrentSort(value)

  const handleSetAllGantt = (value: ConfigRowsType[]) => setAllGantt(value)

  const handleShowDialog = (data: DataOfCopiedModelType) => {
    setCopyGanttData(data)
    setShowCopyDialog(true)
  }

  const handleCloseDialog = () => {
    setCopyGanttData(null)
    setShowCopyDialog(false)
  }

  const handleCopyGantt = (data: CopyModelDataType) => {
    const { title, copyId, code } = data
    copyGantt({ id: copyId, code, title })
      .unwrap()
      .then(id => {
        setShowCopyDialog(false)
        if (id) {
          navigate(`${ROUTES.CONFIG_GANTT_EDIT}/${id}`)
        }
      })
  }

  return {
    state: {
      objectCode,
      searchPattern,
      currentSort,
      allGantt,
      showCopyDialog,
      copyGanttData,
    },
    handlers: {
      handleDeleteGantt,
      handleDeleteGanttByCode,
      handleEditGantt,
      handleSetSorting,
      handleSetObjectCode,
      handleSetSearchPattern,
      handleSetCurrentSort,
      handleSetAllGantt,
      handleShowDialog,
      handleCloseDialog,
      handleCopyGantt,
    },
  }
}
